import jsPDF from "jspdf";

const generateLegalResearchPDF = (title, dynamicData) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginLeft = 10;
  const marginRight = 10;
  const labelColumnWidth = pageWidth / 3; // Left column for labels
  const valueColumnLeft = labelColumnWidth + 20; // Right column for values
  const sectionSpacing = 12; // Reduced space between sections
  const lineSpacing = 4; // Reduced space between lines
  let currentY = 30; // Adjust starting position to fit better for each form

  // Add watermark
  const addWatermark = (doc, pageWidth, pageHeight) => {
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.1 })); // Set transparency
    doc.setFontSize(100);
    doc.setFont("times", "normal");
    doc.setTextColor(180, 180, 180);
    doc.text("Inmate Inbox", pageWidth / 2, pageHeight / 2, {
      align: "center",
      angle: 30,
    });
    doc.restoreGraphicsState();
  };

  // Check if enough space is available on the page
  const checkSpace = (requiredSpace) => {
    const remainingSpace = pageHeight - currentY - sectionSpacing;
    if (remainingSpace < requiredSpace) {
      doc.addPage(); // Add new page if not enough space
      currentY = 20; // Reset Y position for new page
    }
  };

  // Title Section for each form
  const generateForm = (data) => {
    let currentY = 30;

    // Title Section
    doc.setFont("Times New Roman", "regular");
    doc.setFontSize(16);
    doc.text("Inmate Inbox", pageWidth / 2, 15, { align: "center" });
    doc.setFontSize(17);
    doc.setFont("Times New Roman", "bold");
    doc.text(title, pageWidth / 2, 25, { align: "center" });

    // Inmate Details Section
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 145, 2); // Red color for labels
    doc.text("Name:", marginLeft, currentY);
    doc.setFont("helvetica", "normal");
    doc.text(
      data.firstName + " " + data.lastName || "N/A",
      valueColumnLeft,
      currentY
    );
    currentY += lineSpacing;

    doc.setFont("helvetica", "bold");
    doc.text("Resident ID:", marginLeft, currentY);
    doc.setFont("helvetica", "normal");
    doc.text(data.inmateId || "N/A", valueColumnLeft, currentY);
    currentY += sectionSpacing;

    // Reset text color to black for the rest of the content
    doc.setTextColor(0, 0, 0);

    // Line Separator
    currentY -= 5;
    doc.line(marginLeft, currentY, pageWidth - marginRight, currentY);
    currentY += lineSpacing;

    // Set up the left and right content for the form (labels and dynamic values)
    const labels = [
      "Reference ID",
      "Last Name",
      "First Name",
      "Middle Initial",
      "Inmate ID",
      "Housing Unit",
      "Who sent you to this prison?",
      "Specify 'Other'",
      "Next court date, if known (Month/Day/Year)",
      "List of current charges",
      "Have you been convicted?",
      "If convicted, have you been sentenced?",
      "The matter I am researching has to do with...",
      "Are you 'Pro Se' in the matter you are researching? ('Pro Se' means you are representing yourself.)",
      "If not 'Pro Se', do you have a lawyer currently representing you in this matter?",
      "The lawyer representing you (if any) is… ",
      "What is the jurisdiction of the legal matter you are researching? Federal(Civil)",
      "Please indicate which 'Other State' if applicable.",
      "What information would you like to receive? List up to five (5) items:",
    ];

    const values = [
      data.referenceId,
      data.lastName,
      data.firstName,
      data.middleName,
      data.inmateId,
      data.housingUnit,
      data.whoSent,
      data.specifyOthers,
      data.courtDate,
      data.charges,
      data.IsConvicted,
      data.sentenced,
      data.researchMatter,
      data.IsProSe,
      data.IsProSes,
      data.IfIsLawyerRepresentText,
      data.jurisdiction,
      data.otherState,
      data.infoToReceive,
    ];

    let yPosition = currentY + 10;

    // Left Side Content (Labels and Values)
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");

    labels.forEach((label, index) => {
      const maxLabelWidth = 120; // Width for label column
      const colonWidth = 5; // Space for colon
      const paddingBetweenRows = 3; // ~4px padding in points
      const valueMaxWidth =
        pageWidth - marginLeft - maxLabelWidth - colonWidth - 20;

      if (
        label ===
        "What information would you like to receive? List up to five (5) items:"
      ) {
        const wrappedLabel = doc.splitTextToSize(label, valueMaxWidth);
        wrappedLabel.forEach((line) => {
          doc.text(line, marginLeft, yPosition);
          yPosition += lineSpacing;
        });

        const infoValues = [
          `1. ${data.Information1}`,
          `2. ${data.Information2}`,
          `3. ${data.Information3}`,
          `4. ${data.Information4}`,
          `5. ${data.Information5}`,
        ];

        infoValues.forEach((infoValue) => {
          doc.text(
            infoValue,
            // marginLeft + maxLabelWidth + colonWidth,
            marginLeft,
            yPosition + 3
          );
          yPosition += lineSpacing + 3;
        });

        // Add padding after this block
        yPosition += paddingBetweenRows;
      } else {
        const wrappedLabel = doc.splitTextToSize(label, maxLabelWidth);
        const labelLineCount = wrappedLabel.length;

        const text = values[index] || "-";
        const wrappedText = doc.splitTextToSize(text, valueMaxWidth);
        const valueLineCount = wrappedText.length;

        const totalLines = Math.max(labelLineCount, valueLineCount);
        checkSpace(totalLines * lineSpacing + paddingBetweenRows);

        // Print label
        wrappedLabel.forEach((line, i) => {
          doc.text(line, marginLeft, yPosition + i * lineSpacing);
        });

        // Print colon (aligned with first label line)
        doc.text(":", marginLeft + maxLabelWidth, yPosition);

        // Print value
        wrappedText.forEach((line, i) => {
          doc.text(
            line,
            marginLeft + maxLabelWidth + colonWidth,
            yPosition + i * lineSpacing
          );
        });

        yPosition += totalLines * lineSpacing;

        // Add 4px space between each line set
        yPosition += paddingBetweenRows;
      }
    });

    // Add watermark
    addWatermark(doc, pageWidth, pageHeight);

    // Add page break for multiple forms if there are more entries in the array
    if (dynamicData.indexOf(data) !== dynamicData.length - 1) {
      doc.addPage();
      currentY = 20; // Reset starting position for next page
    }
  };

  // Generate form for each dynamic entry
  dynamicData.forEach((data) => generateForm(data));

  // Save the PDF
  doc.save("legal_research_form.pdf");
};

export default generateLegalResearchPDF;
